'use client'

import { useRouter } from 'next/navigation'
import { nanoid } from 'nanoid'
import { useEffect, useRef, useState } from 'react'

export default function Home() {
  const router = useRouter()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  const [showJoinInput, setShowJoinInput] = useState(false)
  const [joinCode, setJoinCode] = useState('')

  const createRandomMeeting = () => {
    const randomId = nanoid(10)
    router.push(`/meet/${randomId}`)
  }

  const joinMeeting = () => {
    const code = joinCode.trim()
    if (!code) return
    router.push(`/meet/${code}`)
  }

  const handleJoinKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') joinMeeting()
    if (e.key === 'Escape') { setShowJoinInput(false); setJoinCode('') }
  }

  // Animated particle globe on canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animFrame: number
    let t = 0

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio
      canvas.height = canvas.offsetHeight * window.devicePixelRatio
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
    }
    resize()
    window.addEventListener('resize', resize)

    const dots: { lat: number; lon: number; size: number; delay: number }[] = []
    for (let i = 0; i < 180; i++) {
      dots.push({
        lat: (Math.random() - 0.5) * Math.PI,
        lon: Math.random() * Math.PI * 2,
        size: Math.random() * 2 + 0.5,
        delay: Math.random() * Math.PI * 2,
      })
    }

    const connections = [
      { from: 0, to: 12 }, { from: 5, to: 30 }, { from: 20, to: 45 },
      { from: 8, to: 60 }, { from: 15, to: 80 }, { from: 35, to: 100 },
    ]

    const draw = () => {
      const w = canvas.offsetWidth
      const h = canvas.offsetHeight
      ctx.clearRect(0, 0, w, h)

      const cx = w / 2
      const cy = h / 2
      const r = Math.min(w, h) * 0.38

      t += 0.004

      // Draw globe rings
      for (let ring = 0; ring < 6; ring++) {
        const angle = (ring / 6) * Math.PI
        const ry = Math.abs(Math.sin(angle)) * r
        const opacity = 0.06 + Math.sin(angle) * 0.06
        ctx.beginPath()
        ctx.ellipse(cx, cy, r, ry, 0, 0, Math.PI * 2)
        ctx.strokeStyle = `rgba(56, 189, 248, ${opacity})`
        ctx.lineWidth = 1
        ctx.stroke()
      }

      // Vertical lines
      for (let v = 0; v < 8; v++) {
        const a = (v / 8) * Math.PI * 2 + t * 0.3
        const x1 = cx + Math.cos(a) * r
        const y1 = cy
        ctx.beginPath()
        ctx.moveTo(x1, y1 - r * 0.1)
        ctx.bezierCurveTo(x1 + Math.sin(a) * 20, cy - r * 0.5, x1 - Math.sin(a) * 20, cy + r * 0.5, x1, y1 + r * 0.1)
        ctx.strokeStyle = `rgba(56, 189, 248, 0.04)`
        ctx.stroke()
      }

      // Draw dots
      const projectedDots: { x: number; y: number; z: number; size: number; i: number }[] = []
      dots.forEach((dot, i) => {
        const lon = dot.lon + t
        const x3d = Math.cos(dot.lat) * Math.cos(lon)
        const y3d = Math.sin(dot.lat)
        const z3d = Math.cos(dot.lat) * Math.sin(lon)

        const scale = (z3d + 1.5) / 2.5
        const px = cx + x3d * r
        const py = cy - y3d * r

        if (z3d > -0.3) {
          projectedDots.push({ x: px, y: py, z: z3d, size: dot.size * scale, i })
          const alpha = scale * 0.8
          const pulse = 0.5 + 0.5 * Math.sin(t * 3 + dot.delay)
          ctx.beginPath()
          ctx.arc(px, py, dot.size * scale * (1 + pulse * 0.3), 0, Math.PI * 2)
          ctx.fillStyle = `rgba(56, 189, 248, ${alpha * 0.9})`
          ctx.fill()

          if (scale > 0.8 && Math.random() > 0.995) {
            ctx.beginPath()
            ctx.arc(px, py, dot.size * scale * 4, 0, Math.PI * 2)
            ctx.fillStyle = `rgba(56, 189, 248, 0.15)`
            ctx.fill()
          }
        }
      })

      // Draw connections (animated arcs)
      connections.forEach(({ from, to }, ci) => {
        const a = projectedDots[from % projectedDots.length]
        const b = projectedDots[to % projectedDots.length]
        if (!a || !b) return

        const progress = ((t * 0.5 + ci * 0.3) % 1)
        const mx = (a.x + b.x) / 2
        const my = (a.y + b.y) / 2 - 50

        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.quadraticCurveTo(mx, my, b.x, b.y)
        ctx.strokeStyle = `rgba(20, 220, 180, 0.25)`
        ctx.lineWidth = 1.5
        ctx.setLineDash([5, 8])
        ctx.lineDashOffset = -t * 30
        ctx.stroke()
        ctx.setLineDash([])

        // Traveling dot
        const bx = a.x + 2 * (1 - progress) * progress * (mx - a.x) + progress * progress * (b.x - a.x)
        const by = a.y + 2 * (1 - progress) * progress * (my - a.y) + progress * progress * (b.y - a.y)
        ctx.beginPath()
        ctx.arc(bx, by, 3, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(20, 220, 180, 0.9)`
        ctx.fill()
      })

      animFrame = requestAnimationFrame(draw)
    }
    draw()

    return () => {
      cancelAnimationFrame(animFrame)
      window.removeEventListener('resize', resize)
    }
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => setMousePos({ x: e.clientX, y: e.clientY })
    window.addEventListener('mousemove', handler)
    return () => window.removeEventListener('mousemove', handler)
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --ink: #1f2229;
          --sky: #38bdf8;
          --teal: #14dcb4;
          --gold: #f0b429;
          --glass: rgba(255,255,255,0.04);
          --border: rgba(255,255,255,0.08);
        }

        html { scroll-behavior: smooth; height: auto; overflow-y: auto; }
        body {
          background: var(--ink);
          color: #e2e8f0;
          font-family: 'DM Sans', sans-serif;
          overflow-x: hidden;
          overflow-y: auto !important;
          min-height: 100vh;
          height: auto;
          position: relative;
        }

        /* --- CURSOR GLOW --- */
        .cursor-glow {
          pointer-events: none;
          position: fixed;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          z-index: 0;
          transition: left 0.15s ease, top 0.15s ease;
        }

        /* --- NAV --- */
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          z-index: 100;
          padding: 20px 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          background: rgba(31,34,41,0.75);
          backdrop-filter: blur(20px);
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 0;
          text-decoration: none;
        }

        .nav-logo img {
          height: 70px;
          width: auto;
          object-fit: contain;
          display: block;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 32px;
          list-style: none;
        }

        .nav-links a {
          color: #94a3b8;
          text-decoration: none;
          font-size: 0.875rem;
          font-weight: 400;
          letter-spacing: 0.01em;
          transition: color 0.2s;
        }

        .nav-links a:hover { color: #fff; }

        .nav-cta {
          padding: 9px 22px;
          background: rgba(56,189,248,0.12);
          border: 1px solid rgba(56,189,248,0.3);
          border-radius: 40px;
          color: var(--sky) !important;
          font-weight: 500 !important;
          transition: all 0.2s !important;
        }

        .nav-cta:hover {
          background: rgba(56,189,248,0.2) !important;
          color: #fff !important;
        }

        /* --- HERO --- */
        .hero {
          min-height: auto;
          height: auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: center;
          gap: 40px;
          padding: 120px 80px 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 60% 50% at 70% 50%, rgba(56,189,248,0.07) 0%, transparent 70%),
            radial-gradient(ellipse 40% 60% at 10% 80%, rgba(20,220,180,0.05) 0%, transparent 60%);
        }

        .hero-text { position: relative; z-index: 2; }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px;
          background: rgba(56,189,248,0.08);
          border: 1px solid rgba(56,189,248,0.2);
          border-radius: 40px;
          font-size: 0.78rem;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--sky);
          margin-bottom: 28px;
        }

        .eyebrow-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--teal);
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }

        .hero h1 {
          font-family: 'Syne', sans-serif;
          font-size: clamp(3rem, 5vw, 5rem);
          font-weight: 800;
          line-height: 1.05;
          letter-spacing: -0.04em;
          color: #fff;
          margin-bottom: 24px;
        }

        .hero h1 em {
          font-style: normal;
          background: linear-gradient(135deg, var(--sky) 0%, var(--teal) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero-sub {
          font-size: 1.1rem;
          line-height: 1.7;
          color: #64748b;
          max-width: 480px;
          margin-bottom: 40px;
          font-weight: 300;
        }

        .hero-sub strong {
          color: #94a3b8;
          font-weight: 500;
        }

        .hero-actions {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 15px 32px;
          background: linear-gradient(135deg, var(--sky), var(--teal));
          border: none;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 600;
          color: #05060f;
          cursor: pointer;
          transition: all 0.25s;
          box-shadow: 0 0 0 0 rgba(56,189,248,0);
          font-family: 'DM Sans', sans-serif;
          position: relative;
          overflow: hidden;
        }

        .btn-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, transparent, rgba(255,255,255,0.15));
          opacity: 0;
          transition: opacity 0.2s;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 40px -8px rgba(56,189,248,0.5);
        }

        .btn-primary:hover::after { opacity: 1; }

        .btn-primary:active { transform: translateY(0); }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: transparent;
          border: 1px solid var(--border);
          border-radius: 50px;
          font-size: 0.95rem;
          font-weight: 500;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }

        .btn-secondary:hover {
          border-color: rgba(255,255,255,0.2);
          color: #fff;
          background: rgba(255,255,255,0.04);
        }

        /* --- JOIN CODE INPUT --- */
        .join-input-row {
          display: flex;
          align-items: center;
          gap: 8px;
          animation: fadeUp 0.2s ease;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .join-input {
          padding: 14px 20px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(56,189,248,0.3);
          border-radius: 50px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          color: #fff;
          outline: none;
          width: 220px;
          transition: all 0.2s;
          letter-spacing: 0.04em;
        }

        .join-input::placeholder { color: #475569; }

        .join-input:focus {
          border-color: var(--sky);
          background: rgba(56,189,248,0.07);
          box-shadow: 0 0 0 3px rgba(56,189,248,0.12);
        }

        .join-go-btn {
          padding: 14px 22px;
          background: linear-gradient(135deg, var(--sky), var(--teal));
          border: none;
          border-radius: 50px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: #1f2229;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .join-go-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 24px -4px rgba(56,189,248,0.45);
        }

        .join-cancel-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid var(--border);
          background: transparent;
          color: #475569;
          font-size: 1.1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .join-cancel-btn:hover {
          border-color: rgba(255,255,255,0.2);
          color: #fff;
        }

        .hero-langs {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 36px;
        }

        .hero-langs-label {
          font-size: 0.8rem;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .lang-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 12px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border);
          border-radius: 40px;
          font-size: 0.78rem;
          color: #64748b;
          transition: all 0.2s;
        }

        .lang-chip:hover {
          background: rgba(56,189,248,0.08);
          border-color: rgba(56,189,248,0.25);
          color: var(--sky);
        }

        /* --- GLOBE --- */
        .hero-globe {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .globe-wrapper {
          position: relative;
          width: 100%;
          max-width: 520px;
          aspect-ratio: 1;
        }

        .globe-canvas {
          width: 100%;
          height: 100%;
        }

        .globe-glow {
          position: absolute;
          inset: -20%;
          background: radial-gradient(circle, rgba(56,189,248,0.12) 0%, transparent 65%);
          pointer-events: none;
        }

        /* Translation cards floating */
        .translation-card {
          position: absolute;
          background: rgba(31,34,41,0.92);
          backdrop-filter: blur(12px);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 12px 18px;
          font-size: 0.82rem;
          max-width: 180px;
          animation: float 4s ease-in-out infinite;
          box-shadow: 0 20px 40px rgba(0,0,0,0.4);
        }

        .translation-card .lang-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--teal);
          margin-bottom: 4px;
          font-weight: 600;
        }

        .translation-card .card-text {
          color: #e2e8f0;
          line-height: 1.4;
          font-weight: 400;
        }

        .tc-1 { top: 10%; left: -5%; animation-delay: 0s; }
        .tc-2 { bottom: 18%; right: -5%; animation-delay: 1.5s; }
        .tc-3 { top: 55%; left: -8%; animation-delay: 0.8s; }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        /* --- STATS STRIP --- */
        .stats-strip {
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          padding: 32px 80px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          background: rgba(255,255,255,0.01);
        }

        .stat-item {
          text-align: center;
          padding: 8px 20px;
          border-right: 1px solid var(--border);
        }

        .stat-item:last-child { border-right: none; }

        .stat-num {
          font-family: 'Syne', sans-serif;
          font-size: 2.2rem;
          font-weight: 800;
          background: linear-gradient(135deg, var(--sky), var(--teal));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          line-height: 1;
          margin-bottom: 6px;
        }

        .stat-label {
          font-size: 0.8rem;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        /* --- FEATURES --- */
        .section {
          padding: 100px 80px;
          position: relative;
        }

        .section-header {
          max-width: 600px;
          margin: 0 auto 70px;
          text-align: center;
        }

        .section-tag {
          display: inline-block;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--sky);
          font-weight: 600;
          margin-bottom: 16px;
        }

        .section-title {
          font-family: 'Syne', sans-serif;
          font-size: clamp(2rem, 3.5vw, 3rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #fff;
          line-height: 1.1;
          margin-bottom: 16px;
        }

        .section-desc {
          color: #475569;
          font-size: 1rem;
          line-height: 1.6;
        }

        .features-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          grid-template-rows: auto auto;
          gap: 16px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .feature-card {
          background: var(--glass);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 36px;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at top left, rgba(56,189,248,0.05) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .feature-card:hover {
          border-color: rgba(56,189,248,0.2);
          transform: translateY(-4px);
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .feature-card:hover::before { opacity: 1; }

        .feature-card.large {
          grid-column: 1;
          grid-row: 1 / 3;
          display: flex;
          flex-direction: column;
        }

        .feature-icon {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin-bottom: 20px;
          position: relative;
          flex-shrink: 0;
        }

        .icon-blue { background: rgba(56,189,248,0.12); }
        .icon-teal { background: rgba(20,220,180,0.12); }
        .icon-gold { background: rgba(240,180,41,0.12); }
        .icon-purple { background: rgba(167,139,250,0.12); }

        .feature-title {
          font-family: 'Syne', sans-serif;
          font-size: 1.2rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 10px;
          letter-spacing: -0.02em;
        }

        .feature-desc {
          color: #475569;
          font-size: 0.9rem;
          line-height: 1.6;
        }

        /* Large card visual */
        .translation-demo {
          margin-top: auto;
          padding-top: 32px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .demo-msg {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .demo-msg.right { flex-direction: row-reverse; }

        .demo-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          flex-shrink: 0;
        }

        .av-blue { background: rgba(56,189,248,0.2); }
        .av-teal { background: rgba(20,220,180,0.2); }

        .demo-bubble {
          background: rgba(255,255,255,0.06);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 10px 14px;
          max-width: 200px;
        }

        .demo-bubble .original {
          font-size: 0.78rem;
          color: #94a3b8;
          margin-bottom: 4px;
          font-style: italic;
        }

        .demo-bubble .translated {
          font-size: 0.85rem;
          color: #e2e8f0;
        }

        .demo-arrow {
          text-align: center;
          color: var(--teal);
          font-size: 0.75rem;
          opacity: 0.6;
        }

        /* Waveform visual */
        .waveform {
          display: flex;
          align-items: center;
          gap: 3px;
          height: 40px;
          margin-top: 20px;
        }

        .wave-bar {
          width: 3px;
          border-radius: 2px;
          background: linear-gradient(to top, var(--sky), var(--teal));
          animation: wave 1.2s ease-in-out infinite;
        }

        @keyframes wave {
          0%, 100% { height: 8px; opacity: 0.4; }
          50% { height: var(--h, 30px); opacity: 1; }
        }

        /* --- HOW IT WORKS --- */
        .how-section {
          padding: 100px 80px;
          background: rgba(255,255,255,0.01);
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
        }

        .steps {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          max-width: 1000px;
          margin: 0 auto;
          position: relative;
        }

        .steps::before {
          content: '';
          position: absolute;
          top: 24px;
          left: 12%;
          width: 76%;
          height: 1px;
          background: linear-gradient(to right, transparent, var(--sky), var(--teal), transparent);
          opacity: 0.3;
        }

        .step {
          text-align: center;
          padding: 0 24px;
          position: relative;
        }

        .step-num {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 1px solid rgba(56,189,248,0.3);
          background: rgba(56,189,248,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Syne', sans-serif;
          font-weight: 800;
          font-size: 1rem;
          color: var(--sky);
          margin: 0 auto 20px;
          position: relative;
          z-index: 1;
        }

        .step-icon {
          font-size: 1.5rem;
          margin-bottom: 12px;
          display: block;
        }

        .step h3 {
          font-family: 'Syne', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 8px;
        }

        .step p {
          font-size: 0.83rem;
          color: #475569;
          line-height: 1.5;
        }

        /* --- FOOTER --- */
        footer {
          padding: 48px 80px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top: 1px solid var(--border);
        }

        .footer-brand {
          display: flex;
          align-items: center;
        }

        .footer-brand img {
          height: 70px;
          width: auto;
          object-fit: contain;
          opacity: 0.85;
          transition: opacity 0.2s;
        }

        .footer-brand img:hover { opacity: 1; }

        .footer-links {
          display: flex;
          gap: 28px;
          list-style: none;
        }

        .footer-links a {
          color: #475569;
          text-decoration: none;
          font-size: 0.83rem;
          transition: color 0.2s;
        }

        .footer-links a:hover { color: #94a3b8; }

        .footer-copy {
          font-size: 0.78rem;
          color: #334155;
        }

        /* ========== RESPONSIVE MEDIA QUERIES (avec scroll fix) ========== */
        @media (max-width: 1024px) {
          .hero {
            grid-template-columns: 1fr;
            padding: 100px 40px 60px;
            text-align: center;
          }
          .hero-text { order: 2; }
          .hero-globe { display: block !important; margin-bottom: 40px; order: 1; }
          .globe-wrapper { max-width: 400px; margin: 0 auto; }
          .hero-sub { margin-left: auto; margin-right: auto; }
          .hero-actions { justify-content: center; }
          .hero-langs { justify-content: center; }
          .features-grid { grid-template-columns: 1fr 1fr; }
          .feature-card.large { grid-column: 1 / 3; grid-row: auto; }
          .stats-strip { grid-template-columns: repeat(2, 1fr); padding: 32px 40px; }
          .stat-item:nth-child(2) { border-right: none; }
          .steps { grid-template-columns: repeat(2, 1fr); gap: 40px; }
          .steps::before { display: none; }
          .section, .how-section { padding: 70px 40px; }
          footer { flex-direction: column; gap: 24px; text-align: center; }
          nav { padding: 16px 24px; }
          [style*="display: grid"][style*="1fr auto 1fr"] { grid-template-columns: 1fr !important; gap: 40px !important; }
          [style*="center divider"] { display: none !important; }
        }

        @media (max-width: 768px) {
          .hero { padding: 80px 20px 40px; }
          .hero h1 { font-size: 2.5rem; }
          .hero-sub { font-size: 1rem; padding: 0 10px; }
          .nav-links { display: none !important; }
          .globe-wrapper { max-width: 280px; }
          .features-grid { grid-template-columns: 1fr; }
          .feature-card.large { grid-column: 1; }
          .stats-strip { grid-template-columns: 1fr; padding: 20px; gap: 20px; }
          .stat-item { border-right: none; border-bottom: 1px solid var(--border); padding: 15px 20px; }
          .stat-item:last-child { border-bottom: none; }
          .steps { grid-template-columns: 1fr; gap: 30px; }
          .section, .how-section { padding: 50px 20px; }
          .section-title { font-size: 1.8rem; }
          .feature-card { padding: 24px; }
          .translation-card { display: none !important; }
          .btn-primary, .btn-secondary { padding: 12px 24px; font-size: 0.9rem; }
          .join-input { width: 160px; padding: 12px 16px; }
          .hero-actions { flex-direction: column; width: 100%; }
          .hero-actions button { width: 100%; justify-content: center; }
          .join-input-row { width: 100%; }
          .join-input { flex: 1; }
          .footer-links { flex-wrap: wrap; justify-content: center; gap: 20px; }
          .lang-chip { font-size: 0.7rem; padding: 4px 10px; }
        }

        @media (max-width: 480px) {
          .hero h1 { font-size: 2rem; }
          .hero-eyebrow { font-size: 0.7rem; }
          .btn-primary, .btn-secondary { width: 100%; }
          .feature-title { font-size: 1rem; }
          .feature-desc { font-size: 0.8rem; }
          .stat-num { font-size: 1.8rem; }
          .stat-label { font-size: 0.7rem; }
          .footer-brand img { height: 50px; }
        }
         /* Styles pour la section "Comment ça marche" */
.how-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 32px;
  max-width: 1000px;
  margin: 0 auto;
  align-items: start;
}

.how-divider {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 64px;
  gap: 0;
}

/* Responsive : tablette et mobile */
@media (max-width: 1024px) {
  .how-grid {
    grid-template-columns: 1fr !important;
    gap: 40px !important;
  }
  .how-divider {
    display: none !important;
  }
} 
      `}</style>

      <div className="app-container" style={{ minHeight: '100vh', height: 'auto', position: 'relative', overflow: 'visible' }}>
        {/* Cursor glow */}
        <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

        {/* NAV */}
        <nav>
          <a href="/" className="nav-logo">
            <img src="/logo.png" alt="WIC Talk" />
          </a>
          <ul className="nav-links">
            <li><a href="#features">Fonctionnalités</a></li>
            <li><a href="#how">Comment ça marche</a></li>
            <li><a href="#" className="nav-cta" onClick={createRandomMeeting}>Démarrer une réunion</a></li>
          </ul>
        </nav>

        {/* HERO */}
        <section className="hero">
          <div className="hero-text">
            <div className="hero-eyebrow">
              <span className="eyebrow-dot" />
              Traduction IA en direct · Sans compte requis
            </div>

            <h1>
              Rencontrez n'importe qui,<br />
              <em>parlez librement.</em>
            </h1>

            <p className="hero-sub">
              WIC Talk brise les barrières linguistiques en temps réel. <strong>Des réunions vidéo HD</strong> avec
              sous-titres IA en direct — pour que chaque participant vous entende dans sa propre langue.
            </p>

            <div className="hero-actions">
              <button className="btn-primary" onClick={createRandomMeeting}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 10l5-5m0 0h-5m5 0v5" />
                  <path d="M9 14l-5 5m0 0h5m-5 0v-5" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Nouvelle réunion
              </button>

              {!showJoinInput ? (
                <button className="btn-secondary" onClick={() => setShowJoinInput(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  Rejoindre avec un code
                </button>
              ) : (
                <div className="join-input-row">
                  <input
                    className="join-input"
                    type="text"
                    placeholder="Code de la réunion…"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value)}
                    onKeyDown={handleJoinKeyDown}
                    autoFocus
                  />
                  <button className="join-go-btn" onClick={joinMeeting}>
                    Rejoindre →
                  </button>
                  <button
                    className="join-cancel-btn"
                    onClick={() => { setShowJoinInput(false); setJoinCode('') }}
                    title="Annuler"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <div className="hero-langs">
              <span className="hero-langs-label">Langues disponibles</span>
              {[
                { flag: '🇫🇷', label: 'FR' },
                { flag: '🇮🇹', label: 'IT' },
              ].map(l => (
                <span key={l.label} className="lang-chip">{l.flag} {l.label}</span>
              ))}
            </div>
          </div>

          {/* Globe */}
          <div className="hero-globe">
            <div className="globe-wrapper">
              <div className="globe-glow" />
              <canvas ref={canvasRef} className="globe-canvas" />

              {/* Floating translation cards */}
              <div className="translation-card tc-1">
                <div className="lang-label">🇫🇷 Français</div>
                <div className="card-text">Bonjour, comment allez-vous ?</div>
              </div>
              <div className="translation-card tc-2">
                <div className="lang-label">🇮🇹 Italiano</div>
                <div className="card-text">Ciao, come stai oggi?</div>
              </div>
              <div className="translation-card tc-3">
                <div className="lang-label">🇬🇧 English</div>
                <div className="card-text">Hello, how are you?</div>
              </div>
            </div>
          </div>
        </section>

        {/* STATS STRIP */}
        <div className="stats-strip">
          {[
            { num: '< 1s', label: 'Latence de traduction' },
            { num: '6+', label: 'Langues supportées' },
            { num: '100%', label: 'Chiffrement de bout en bout' },
            { num: 'Gratuit', label: 'Sans compte requis' },
          ].map((s, i) => (
            <div className="stat-item" key={i}>
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* FEATURES */}
        <section className="section" id="features">
          <div className="section-header">
            <div className="section-tag">Pourquoi WIC Talk</div>
            <h2 className="section-title">Tout ce dont vous avez besoin,<br />rien de superflu</h2>
            <p className="section-desc">Conçu pour la clarté et la rapidité — rejoignez une réunion en quelques secondes et laissez l'IA gérer la langue.</p>
          </div>

          <div className="features-grid">
            <div className="feature-card large">
              <div className="feature-icon icon-blue">🌐</div>
              <div className="feature-title">Traduction IA en temps réel</div>
              <div className="feature-desc">
                Notre moteur ASR capture votre voix et la traduit en direct. Les participants voient les sous-titres
                dans leur propre langue avec moins d'une seconde de latence — sans décalage, sans pauses gênantes.
              </div>

              <div className="translation-demo">
                <div className="demo-msg">
                  <div className="demo-avatar av-blue">👨‍⚕️</div>
                  <div className="demo-bubble">
                    <div className="original">🇫🇷 "Le traitement est efficace."</div>
                    <div className="translated">🇮🇹 "Il trattamento è efficace."</div>
                  </div>
                </div>
                <div className="demo-arrow">↕ traduit instantanément</div>
                <div className="demo-msg right">
                  <div className="demo-avatar av-teal">👩‍💼</div>
                  <div className="demo-bubble">
                    <div className="original">🇮🇹 "Capisco, grazie dottore."</div>
                    <div className="translated">🇫🇷 "Je comprends, merci docteur."</div>
                  </div>
                </div>

                <div className="waveform">
                  {Array.from({ length: 24 }).map((_, i) => (
                    <div
                      key={i}
                      className="wave-bar"
                      style={{
                        '--h': `${8 + Math.abs(Math.sin(i * 0.7)) * 28}px`,
                        animationDelay: `${i * 0.06}s`,
                      } as React.CSSProperties}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon icon-teal">🔒</div>
              <div className="feature-title">Chiffrement de bout en bout</div>
              <div className="feature-desc">
                Tous les flux vidéo, audio et de traduction sont chiffrés. Vos conversations médicales restent totalement privées.
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon icon-gold">⚡</div>
              <div className="feature-title">Instantané, sans inscription</div>
              <div className="feature-desc">
                Générez un lien de réunion unique en un clic. Partagez-le — c'est tout ce qu'il faut pour commencer.
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon icon-purple">🎙️</div>
              <div className="feature-title">Audio et vidéo HD</div>
              <div className="feature-desc">
                Propulsé par Jitsi. Des appels d'une clarté cristalline avec suppression d'écho et réduction du bruit intégrées.
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon icon-blue">📱</div>
              <div className="feature-title">Fonctionne partout</div>
              <div className="feature-desc">
                Natif au navigateur — aucune application à télécharger. Fonctionne sur mobile, tablette et ordinateur.
              </div>
            </div>
          </div>
        </section>

        {/* COMMENT ÇA MARCHE */}
        <section className="how-section" id="how">
          <div className="how-grid">
            <div className="section-tag">Comment ça marche</div>
            <h2 className="section-title">Deux parcours, une seule expérience</h2>
            <p className="section-desc">Que vous organisiez ou rejoigniez une réunion, le processus est simple et instantané.</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '0 32px',
            maxWidth: '1000px',
            margin: '0 auto',
            alignItems: 'start',
          }}>
            {/* Left column: Organisateur */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 20px',
                background: 'rgba(56,189,248,0.08)',
                border: '1px solid rgba(56,189,248,0.25)',
                borderRadius: 40,
                marginBottom: 32,
                alignSelf: 'flex-start',
              }}>
                <span style={{ fontSize: '1.1rem' }}>🧑‍💼</span>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#38bdf8' }}>
                  L'organisateur
                </span>
              </div>

              {[
                { num: '01', icon: '✨', title: 'Créer la réunion', desc: 'Cliquez sur "Nouvelle réunion" — un lien unique est généré en un instant, sans inscription.' },
                { num: '02', icon: '🔗', title: 'Partager l\'invitation', desc: 'Copiez et envoyez le lien, le QR code ou simplement le code de la réunion à vos participants.' },
                { num: '03', icon: '🌐', title: 'Choisir sa langue', desc: 'Sélectionnez la langue dans laquelle vous allez parler avant d\'entrer dans la salle.' },
                { num: '04', icon: '🎤', title: 'Activer les sous-titres', desc: 'Appuyez sur "Activer les sous-titres" et parlez naturellement — l\'IA traduit en direct.' },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, paddingBottom: 28, position: 'relative' }}>
                  {i < 3 && (
                    <div style={{
                      position: 'absolute',
                      left: 20,
                      top: 48,
                      bottom: 0,
                      width: 1,
                      background: 'linear-gradient(to bottom, rgba(56,189,248,0.3), rgba(56,189,248,0.05))',
                    }} />
                  )}
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(56,189,248,0.1)',
                    border: '1px solid rgba(56,189,248,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.8rem',
                    color: '#38bdf8', position: 'relative', zIndex: 1,
                  }}>
                    {step.num}
                  </div>
                  <div style={{ paddingTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: '1rem' }}>{step.icon}</span>
                      <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
                        {step.title}
                      </h3>
                    </div>
                    <p style={{ fontSize: '0.83rem', color: '#475569', lineHeight: 1.55 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Center divider */}
          <div className="how-divider">
  <div style={{ width: 1, height: 60, background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.08))' }} />
  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>🤝</div>
  <div style={{ width: 1, flexGrow: 1, background: 'linear-gradient(to bottom, rgba(255,255,255,0.08), transparent)' }} />
</div>

            {/* Right column: Participant */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 20px',
                background: 'rgba(20,220,180,0.08)',
                border: '1px solid rgba(20,220,180,0.25)',
                borderRadius: 40,
                marginBottom: 32,
                alignSelf: 'flex-start',
              }}>
                <span style={{ fontSize: '1.1rem' }}>🙋</span>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#14dcb4' }}>
                  Le participant
                </span>
              </div>

              {[
                { num: '01', icon: '📩', title: 'Recevoir l\'invitation', desc: 'Recevez le lien, le QR code ou le code de réunion partagé par l\'organisateur.' },
                { num: '02', icon: '🔑', title: 'Rejoindre la salle', desc: 'Cliquez sur le lien ou entrez le code dans "Rejoindre avec un code" — accès immédiat.' },
                { num: '03', icon: '🌐', title: 'Choisir sa langue', desc: 'Sélectionnez votre langue préférée. Elle peut être différente de celle de l\'organisateur.' },
                { num: '04', icon: '✅', title: 'Communiquer librement', desc: 'Parlez dans votre langue et lisez les sous-titres traduits en temps réel. Aucune barrière.' },
              ].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, paddingBottom: 28, position: 'relative' }}>
                  {i < 3 && (
                    <div style={{
                      position: 'absolute',
                      left: 20,
                      top: 48,
                      bottom: 0,
                      width: 1,
                      background: 'linear-gradient(to bottom, rgba(20,220,180,0.3), rgba(20,220,180,0.05))',
                    }} />
                  )}
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(20,220,180,0.1)',
                    border: '1px solid rgba(20,220,180,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.8rem',
                    color: '#14dcb4', position: 'relative', zIndex: 1,
                  }}>
                    {step.num}
                  </div>
                  <div style={{ paddingTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: '1rem' }}>{step.icon}</span>
                      <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>
                        {step.title}
                      </h3>
                    </div>
                    <p style={{ fontSize: '0.83rem', color: '#475569', lineHeight: 1.55 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            maxWidth: 520,
            margin: '48px auto 0',
            padding: '18px 28px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            textAlign: 'left',
          }}>
            <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>💡</span>
            <p style={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 1.55 }}>
              <strong style={{ color: '#94a3b8' }}>Les deux rôles partagent la même étape finale :</strong>{' '}
              activer les sous-titres IA et parler dans sa langue. WIC Talk s'occupe du reste.
            </p>
          </div>
        </section>

        <section style={{ padding: '100px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(56,189,248,0.07) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🌍</div>
            <h2 style={{
              fontFamily: 'Syne, sans-serif',
              fontSize: 'clamp(2rem, 4vw, 3.5rem)',
              fontWeight: 800,
              letterSpacing: '-0.04em',
              color: '#fff',
              marginBottom: '16px',
            }}>
              Commencez votre première réunion<br />
              <span style={{
                background: 'linear-gradient(135deg, #38bdf8, #14dcb4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>maintenant, gratuitement.</span>
            </h2>
            <p style={{ color: '#475569', marginBottom: '36px', fontSize: '1rem' }}>
              Pas de carte de crédit. Pas de compte. Aucune friction.
            </p>
            <button className="btn-primary" onClick={createRandomMeeting} style={{ margin: '0 auto' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              Créer une réunion gratuite
            </button>
          </div>
        </section>

        {/* FOOTER */}
        <footer>
          <div className="footer-brand">
            <img src="/logo.png" alt="WIC Talk" />
          </div>
          <ul className="footer-links">
            <li><a href="#">Confidentialité</a></li>
            <li><a href="#">Conditions</a></li>
            <li><a href="#">Contact</a></li>
            <li><a href="https://wic-doctor.com">WIC Doctor</a></li>
          </ul>
          <p className="footer-copy">© 2025 WIC Talk. Tous droits réservés.</p>
        </footer>
      </div>
    </>
  )
}